import { NavigateBefore, NavigateNext } from '@mui/icons-material'
import { MediaColumn } from '@renderer-ui-library/molecules'
import { Media } from '@renderer-ui-library/molecules/Media/Media'
import { Grid } from '@renderer-ui-library/mui'
import { useMediaQuery } from '@renderer-ui-library/mui/utils/mediaQuery/useMediaQuery'
import classNames from 'classnames'
import React, { useMemo, useState } from 'react'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import { Navigation } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import { TMediaColumnData } from './IMediaColumnsBlockData'
import styles from './carouselMediaColumns.module.scss'
interface CarouselMediaColumnsProps {
  columns: TMediaColumnData[]
  containsOnlyImages: boolean
  hasCta: boolean
  highlight: boolean
}

export const CarouselMediaColumns: React.FC<CarouselMediaColumnsProps> =
  React.memo(({ columns, containsOnlyImages, hasCta, highlight }) => {
    const { isMobile } = useMediaQuery()
    const [isBeginning, setIsBeginning] = useState<boolean>(true)
    const [isEnd, setIsEnd] = useState<boolean>(false)

    const renderDesktopCarousel = useMemo(
      () => (
        <div className={styles.swiper}>
          <Grid item xs={12} md={12}>
            <Swiper
              modules={[Navigation]}
              freeMode
              spaceBetween={16}
              slidesPerView={'auto'}
              navigation={{
                prevEl: `.${styles.swiperPrev}`,
                nextEl: `.${styles.swiperNext}`,
              }}
              onSlideChange={() => {
                setIsBeginning(false)
                setIsEnd(false)
              }}
              onReachBeginning={() => {
                setIsBeginning(true)
                setIsEnd(false)
              }}
              onReachEnd={() => {
                setIsBeginning(false)
                setIsEnd(true)
              }}
            >
              {columns.map((column) => (
                <SwiperSlide key={column.id} className={styles.swiperSlide}>
                  <MediaColumn
                    id={column.id}
                    title={column.title}
                    content={column.content}
                    cta={column.cta}
                    showInfo
                    noMarginBottom={hasCta}
                    highlight={highlight}
                  >
                    {column.media && (
                      <div className={styles.image}>
                        <Media
                          media={column.media}
                          ratio={containsOnlyImages ? 3 / 2 : 16 / 9}
                        />
                      </div>
                    )}
                  </MediaColumn>
                </SwiperSlide>
              ))}
            </Swiper>
          </Grid>
        </div>
      ),
      [columns, hasCta, highlight, containsOnlyImages]
    )

    const renderMobileCarousel = useMemo(
      () => (
        <div className={styles.mobileWrapper}>
          {columns.map((column) => (
            <div key={column.id} className={styles.column}>
              <MediaColumn
                id={column.id}
                title={column.title}
                content={column.content}
                cta={column.cta}
                showInfo
                noMarginBottom={hasCta}
              >
                {column.media && (
                  <div className={styles.image}>
                    <Media
                      media={column.media}
                      ratio={containsOnlyImages ? 3 / 2 : 16 / 9}
                    />
                  </div>
                )}
              </MediaColumn>
            </div>
          ))}
        </div>
      ),
      [columns, containsOnlyImages, hasCta]
    )
    return isMobile ? (
      renderMobileCarousel
    ) : (
      <div className={styles.wrapper}>
        {renderDesktopCarousel}
        <div
          className={classNames(styles.swiperPrev, {
            [styles.hideNavigate]: isBeginning,
          })}
        >
          <NavigateBefore color='inherit' />
        </div>
        <div
          className={classNames(styles.swiperNext, {
            [styles.hideNavigate]: isEnd,
          })}
        >
          <NavigateNext color='inherit' />
        </div>
      </div>
    )
  })

CarouselMediaColumns.displayName = 'CarouselMediaColumns'
